import React from 'react';
import { Icon, IconName } from './Icon';

interface StatCardProps {
  label: string;
  value: string | number;
  icon: IconName;
  color?: 'green' | 'blue' | 'purple' | 'red' | 'orange';
  trend?: {
    value: number;
    type: 'up' | 'down' | 'neutral';
  };
  // ✨ NOUVEAU : Support mini chart pour analytics
  miniChart?: {
    data: number[];
    showChart: boolean;
  };
  // ✨ NOUVEAU : Description contextuelle
  description?: string;
  // ✨ NOUVEAU : Période pour clarifier
  period?: string;
  // ✨ NOUVEAU : Variant glass pour dashboard vertical
  variant?: 'default' | 'glass';
  className?: string;
  loading?: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  color = 'blue',
  trend,
  miniChart,
  description,
  period,
  variant = 'default',
  className = '',
  loading = false
}) => {
  // Variant Glass : layout vertical centré, épuré
  if (variant === 'glass') {
    return (
      <div className={`stat-card stat-card--glass ${className}`}>
        <div className="stat-card__content">
          {loading ? (
            <div className="stat-card__skeleton">
              <div className="stat-card__skeleton-line stat-card__skeleton-line--glass"></div>
            </div>
          ) : (
            <>
              {/* Icône en arrière-plan + Valeur */}
              <div className="stat-card__value-row">
                <div className={`stat-card__icon stat-card__icon--${color}`}>
                  <Icon name={icon} size={40} />
                </div>
                <p className="stat-card__value">{value}</p>
              </div>

              {/* Label en dessous */}
              <p className="stat-card__label">{label}</p>

              {/* Description contextuelle */}
              {description && (
                <p className="stat-card__description">{description}</p>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Variant Default : layout original
  return (
    <div className={`stat-card ${className}`}>
      <div className="stat-card__content">
        {/* Icon - prominent position */}
        <div className={`stat-card__icon stat-card__icon--${color}`}>
          <Icon name={icon} size={20} />
        </div>

        {/* Info section */}
        <div className="stat-card__info">
          <div className="stat-card__header">
            <p className="stat-card__label">
              {label}
              {period && <span className="stat-card__period">({period})</span>}
            </p>
          </div>

          {loading ? (
            <div className="stat-card__skeleton">
              <div className="stat-card__skeleton-line"></div>
            </div>
          ) : (
            <>
              <p className="stat-card__value">{value}</p>

              {/* Mini Chart ou Description */}
              {miniChart?.showChart && miniChart.data.length > 0 ? (
                <div className="stat-card__mini-chart">
                  <svg viewBox="0 0 100 20" className="stat-card__sparkline">
                    <polyline
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      points={miniChart.data
                        .map((val, index) => {
                          const x = (index / (miniChart.data.length - 1)) * 100;
                          const y = 20 - ((val / Math.max(...miniChart.data)) * 18);
                          return `${x},${y}`;
                        })
                        .join(' ')}
                    />
                  </svg>
                </div>
              ) : description ? (
                <p className="stat-card__description">{description}</p>
              ) : null}

              {/* Trend indicator */}
              {trend && (
                <div className={`stat-card__trend stat-card__trend--${trend.type}`}>
                  <Icon
                    name={
                      trend.type === 'up' ? 'TrendingUp' :
                      trend.type === 'down' ? 'TrendingDown' :
                      'Minus'
                    }
                    size={10}
                  />
                  <span>{trend.value > 0 ? '+' : ''}{trend.value}%</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 